import React, { useRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import useSearch from "../../hooks/useSearch";
import SearchResults from "./SearchResults";

// Mobile search: a round icon button (below lg) that, when tapped,
// tells Navbar to enter "search open" mode. In open mode Navbar hides
// the brand + buttons and shows the expanded bar (rendered here).
export default function MobileSearch({ open, onOpen, onClose }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [query, setQuery] = useState("");
  const { results, clear } = useSearch(query);
  const inputRef = useRef(null);
  const iconBtnRef = useRef(null);

  // Focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setQuery("");
    clear();
    onClose();
    // Return focus to trigger button
    setTimeout(() => {
      if (iconBtnRef.current) iconBtnRef.current.focus();
    }, 0);
  };

  const handleSelect = () => {
    setQuery("");
    clear();
    onClose();
  };

  // When not open, render the trigger icon
  if (!open) {
    return (
      <button
        ref={iconBtnRef}
        onClick={onOpen}
        aria-label={t("Open search")}
        className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#FF43A7]/40 tw-bg-transparent tw-text-[#FF43A7] tw-transition-all hover:tw-bg-[#FF43A7]/15 tw-cursor-pointer tw-shrink-0"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} className="tw-text-base" />
      </button>
    );
  }

  // When open, render the expanded search bar
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-relative">
      <div
        className={`tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-[#FF43A7]/30 tw-px-3 tw-py-1.5 tw-transition-all focus-within:tw-border-[#FF43A7]/70 focus-within:tw-shadow-[0_0_10px_rgba(255,67,167,0.15)] tw-flex-1 tw-min-w-0 ${
          isLight ? "tw-bg-white" : "tw-bg-[#1C0039]/80"
        }`}
      >
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="tw-text-[#FF43A7] tw-text-xs tw-flex-shrink-0"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search...")}
          className="tw-flex-1 tw-bg-transparent tw-border-0 tw-ring-0 focus:tw-outline-none focus:tw-ring-0 tw-text-sm tw-text-white placeholder:tw-text-white/30 tw-min-w-0"
        />
      </div>
      <button
        onClick={handleClose}
        aria-label={t("Close")}
        className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#FF43A7]/40 tw-bg-transparent tw-text-[#FF43A7] tw-transition-all hover:tw-bg-[#FF43A7]/15 tw-cursor-pointer tw-shrink-0"
      >
        <FontAwesomeIcon icon={faXmark} className="tw-text-lg" />
      </button>

      {/* Results dropdown */}
      {results.length > 0 && (
        <SearchResults
          results={results}
          onSelect={handleSelect}
          inverted
          isLight={isLight}
          className="tw-left-0 tw-right-0 tw-max-h-48"
        />
      )}
    </div>
  );
}
